import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Dialog
} from '@material-ui/core'
import SamplePreviewDialog from './room/SamplePreviewDialog'
import { SamplePost } from 'apis/response_types'
import { useDispatch, useSelector } from 'react-redux'
import loadingModules from 'modules/loading'
import alertModules from 'modules/alert'
import {
  GetSamplePostResponse,
  defaultSamplePostResponseValue,
  getSamplePostPreviewV1
} from 'apis/door/PostV1'
import { Link, useParams } from 'react-router-dom'
import samplePostModules, { getSamplePosts } from 'modules/sample_posts'
import { RootState } from 'store'
import {
  createSamplePost,
  deleteSamplePost,
  updateSamplePost
} from 'apis/sample_posts'
import InputErrorText from 'components/common/InputError'
import CloseIcon from '@material-ui/icons/Close'
import CompleteImage from './sample_edit_complete.webp'

interface Props {
  isOpen: boolean
  onClose: Function
  postDynamoId: string
  contentCharCount: number
  callback?: Function
}

export function SamplePostEditModal({
  isOpen,
  onClose,
  postDynamoId,
  contentCharCount,
  callback = () => {}
}: Props) {
  const dispatch = useDispatch()
  let { door_id } = useParams<{
    door_id: string
  }>()
  const [isCharacterLimitEnabled, setIsCharacterLimitEnabled] = useState<
    'on' | 'off'
  >('off')
  const [characterLimit, setCharacterLimit] = useState<number | ''>(100)
  const [characterLimitErrorText, setCharacterLimitErrorText] = useState('')
  const [samplePost, setSamplePost] = useState<GetSamplePostResponse>(
    defaultSamplePostResponseValue
  )
  const [isOpenConfirmSamplePostDialog, setIsOpenConfirmSamplePostDialog] =
    useState<boolean>(false)

  const [isOpenCompleteModal, setIsOpenCompleteModal] = useState<boolean>(false)
  const samplePosts = useSelector((state: RootState) => getSamplePosts(state))
  const [isValid, setIsValid] = useState(false)
  const [editedSamplePostId, setEditedSamplePostId] = useState(0)

  useEffect(() => {
    validate()
  }, [characterLimit])

  useEffect(() => {
    const sample = registerdSamplePost(postDynamoId)
    if (sample && sample.character_limit !== null) {
      setCharacterLimit(sample.character_limit)
      setIsCharacterLimitEnabled('on')
    }
  }, [postDynamoId])

  const confirmSamplePost = async () => {
    try {
      dispatch(loadingModules.actions.start())
      const res = await getSamplePostPreviewV1(
        door_id,
        postDynamoId,
        isCharacterLimitEnabled === 'on' ? characterLimit : ''
      )
      setSamplePost(res.data)
    } finally {
      dispatch(loadingModules.actions.stop())
      setIsOpenConfirmSamplePostDialog(true)
    }
  }

  /**
   * post_dynamo_idがサンプル投稿に登録されている場合は、サンプル投稿を返す
   * @param postDynamoID
   * @returns
   */
  const registerdSamplePost = (
    postDynamoID: string
  ): SamplePost | undefined => {
    if (samplePosts === null) {
      return undefined
    } else {
      return samplePosts.find((sp: SamplePost) => sp.dynamo_id === postDynamoID)
    }
  }

  const handleDeleteDoorSample = async () => {
    try {
      dispatch(loadingModules.actions.start())
      const response = await deleteSamplePost(postDynamoId, door_id)
      dispatch(samplePostModules.actions.setSamplePosts(response.data))
      dispatch(
        alertModules.actions.setAlert({
          text: '投稿サンプルを削除しました',
          severity: 'success'
        })
      )
    } catch (error) {
      dispatch(
        alertModules.actions.setAlert({
          text: '投稿サンプルを削除できませんでした　時間をおいて再度お試しください',
          severity: 'error'
        })
      )
    } finally {
      callback()
      onCloseSamplePostDialog()
      dispatch(loadingModules.actions.stop())
    }
  }

  const onCloseSamplePostDialog = () => {
    onClose()
    setIsOpenConfirmSamplePostDialog(false)
    setSamplePost(defaultSamplePostResponseValue)
  }

  const handleSubmitSamplePost = async () => {
    try {
      dispatch(loadingModules.actions.start())
      const current = registerdSamplePost(postDynamoId)
      const response =
        current !== undefined
          ? await updateSamplePost(current.id, {
              post_dynamo_id: postDynamoId,
              character_limit:
                isCharacterLimitEnabled === 'on' &&
                typeof characterLimit === 'number'
                  ? Number(characterLimit)
                  : undefined,
              door_id
            })
          : await createSamplePost({
              id: postDynamoId,
              character_limit:
                isCharacterLimitEnabled === 'on' &&
                typeof characterLimit === 'number'
                  ? Number(characterLimit)
                  : undefined,
              door_id
            })
      dispatch(samplePostModules.actions.setSamplePosts(response.data))
      const newSample = response.data.find(
        (sp: SamplePost) => sp.dynamo_id === postDynamoId
      )
      if (newSample !== undefined) setEditedSamplePostId(newSample.id)

      onCloseSamplePostDialog()
      setIsOpenCompleteModal(true)
    } catch (error) {
      dispatch(
        alertModules.actions.setAlert({
          text: '投稿サンプルを設定できませんでした　時間をおいて再度お試しください',
          severity: 'error'
        })
      )
    } finally {
      callback()
      dispatch(loadingModules.actions.stop())
    }
  }

  const validate = () => {
    let result = true
    if (isCharacterLimitEnabled === 'on') {
      // 100文字未満か、投稿の文字数より大きい値はエラー
      if (characterLimit !== '' && characterLimit < 100) {
        setCharacterLimitErrorText(
          '表示文字数は100文字以上で入力してください'
        )
        result = false
      } else if (characterLimit !== '' && contentCharCount < characterLimit) {
        setCharacterLimitErrorText(
          `表示文字数は${contentCharCount}文字以下で入力してください`
        )
        result = false
      } else if (characterLimit == '') {
        setCharacterLimitErrorText('表示文字数を指定してください')
        result = false
      } else {
        setCharacterLimitErrorText('')
      }
    }
    setIsValid(result)
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => {
          onClose()
        }}
      >
        <StledCloseIcon
          onClick={() => {
            onClose()
          }}
        />
        <ModalHeader>
          投稿サンプル
          {registerdSamplePost(postDynamoId) !== undefined ? '編集' : '登録'}
        </ModalHeader>

        <ModalContent>
          <StyledRadioGroup
            value={isCharacterLimitEnabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              event.stopPropagation()
              switch (event.target.value) {
                case 'on':
                  setIsCharacterLimitEnabled(event.target.value)
                  return
                case 'off':
                  setIsCharacterLimitEnabled(event.target.value)
                  setIsValid(true)
                  setCharacterLimitErrorText('')
                  setCharacterLimit(100)
                  return
                default:
                  return
              }
            }}
          >
            <FormControlLabel
              control={<Radio value={'off'} color="primary" />}
              label={
                <FormText>
                  全文表示する（添付データも全て表示されます）
                </FormText>
              }
            />

            <StyledFormControlLabel
              disabled={contentCharCount < 100}
              control={<Radio value={'on'} color="primary" />}
              label={
                <FormControlLabelInner>
                  {contentCharCount < 100 ? (
                    <>
                      <FormDisableText>表示文字数を制限する（投稿に添付したファイルは表示されません）</FormDisableText>
                      <TextFieldNotice>※投稿文字数が100文字未満の場合は表示文字数を制限できません</TextFieldNotice>
                    </>
                  ) : (
                    <>
                      <FormText>
                        表示文字数を制限する（投稿に添付したファイルは表示されません）
                      </FormText>
                      <TextFieldWrap>
                        <TextField
                          disabled={isCharacterLimitEnabled === 'off'}
                          type="text"
                          value={characterLimit}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10)
                            if (!isNaN(n)) {
                              setCharacterLimit(n)
                            } else if (e.target.value === '') {
                              setCharacterLimit(e.target.value)
                            }
                          }}
                          variant="outlined"
                        />
                        <TextFieldUnit>文字</TextFieldUnit>
                      </TextFieldWrap>
                      <TextFieldNotice>※100文字以上</TextFieldNotice>
                      <InputErrorText text={characterLimitErrorText} />
                    </>
                  )}
                </FormControlLabelInner>
              }
            />
          </StyledRadioGroup>

          <BtnWrap>
            <SubmitBtn
              disabled={!isValid}
              onClick={() => {
                confirmSamplePost()
              }}
            >
              確認する
            </SubmitBtn>
            {registerdSamplePost.length > 0 && registerdSamplePost(postDynamoId) !== undefined &&
              <CancelBtn onClick={() => handleDeleteDoorSample()}>
                投稿サンプルから削除する
              </CancelBtn>
            }
          </BtnWrap>
        </ModalContent>
      </Modal>
      <SamplePreviewDialog
        doorID={door_id}
        open={isOpenConfirmSamplePostDialog}
        onClose={() => {
          setIsOpenConfirmSamplePostDialog(false)
        }}
        samplePost={samplePost}
        onSubmit={() => {
          handleSubmitSamplePost()
        }}
      />
      <Modal
        open={isOpenCompleteModal}
        onClose={() => {
          setIsOpenCompleteModal(false)
        }}
      >
        <StledCloseIcon
          onClick={() => {
            setIsOpenCompleteModal(false)
          }}
        />
        <ModalHeader>投稿サンプルの設定が完了しました</ModalHeader>
        <ModalContent>
          <ImageWrap>
            <SamplePostCompleteImage src={CompleteImage} />
          </ImageWrap>
          <BtnWrap>
            <LinkBtn to={`/door/${door_id}/samples/${editedSamplePostId}`}>
              投稿サンプルを確認する
            </LinkBtn>
          </BtnWrap>
        </ModalContent>
      </Modal>
    </>
  )
}

const Modal = styled(Dialog)`
  position: relative;
  .MuiDialog-paper {
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    transform: none;
    width: 688px;
    max-width: 688px;
    height: fit-content;
    @media (max-width: 768px) {
      width: 92vw;
    }
  }
`
const StledCloseIcon = styled(CloseIcon)`
  position: absolute;
  right: 12px;
  top: 10px;
  cursor: pointer;
`
const ModalContent = styled.div`
  padding: 24px 40px 32px;
  @media (max-width: 768px) {
    padding: 24px 20px 32px;
  }
`
const ModalHeader = styled.h2`
  padding: 24px 16px;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  border-bottom: 1px solid #c4c4c4;
  line-height: 1;
`
const BtnWrap = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  align-items: center;
  @media (max-width: 640px) {
    gap: 20px;
  }
`

const ImageWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
`
const SamplePostCompleteImage = styled.img`
  width: 202px;
  height: auto;
`

const CancelBtn = styled.button`
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  text-align: center;
  color: #888;
  background: transparent;
  border: 0;
  &:hover {
    opacity: 0.8;
  }
`
const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 48px;
  cursor: pointer;
  text-align: center;
  width: 240px;
  height: 48px;
  border: 0;
  color: #fff;
  background: #45b5f5;
  &:disabled {
    background: #ccc;
    color: #fff;
    &:hover {
      opacity: 1;
    }
    cursor: not-allowed;
  }
  &:hover {
    opacity: 0.8;
  }
`

const LinkBtn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 48px;
  cursor: pointer;
  text-align: center;
  width: 240px;
  height: 48px;
  border: 0;
  color: #fff;
  background: #45b5f5;
  &:hover {
    opacity: 0.8;
  }
`

const StyledRadioGroup = styled(RadioGroup)`
  gap: 24px;
  margin-bottom: 32px;
  .MuiFormControlLabel-root {
    margin-right: 0;
  }
  .MuiTypography-root {
    font-family: "Noto Sans JP", sans-serif;
  }
`
const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  align-items: flex-start;
`
const FormControlLabelInner = styled.div`
  padding-top: 8px;
  @media (max-width: 640px) {
    padding-top: 0;
  }
`
const TextFieldWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  .MuiInputBase-input {
    font-size: 16px;
    color: #333;
    font-size: 16px;
    width: 96px;
    text-align: right;
  }
`
const FormText = styled.p`
  font-size: 14px;
  color: #333;
`

const FormDisableText = styled.p`
  font-size: 14px;
  color: #888;
`
const TextFieldUnit = styled.div`
  font-size: 14px;
  color: #333;
`
const TextFieldNotice = styled.p`
  font-size: 12px;
  margin-top: 8px;
`
